import {
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import {
  RouterLink,
  RouterLinkActive,
  RouterOutlet,
} from '@angular/router';

import { LanguageSwitcher } from '../../components/language-switcher/language-switcher';
import { LanguageService } from '../../i18n/language.service';
import { TranslationKey } from '../../i18n/translations/en';
import { ActiveFarmService } from '../../services/active-farm.service';
import { ActiveGameServerService } from '../../services/active-game-server.service';
import { Dialog } from '../../../shared/ui/dialog/dialog';

@Component({
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    LanguageSwitcher,
    Dialog,
  ],
  selector: 'app-shell',
  templateUrl: './app-shell.html',
  styleUrl: './app-shell.scss',
})
export class AppShell {
  readonly i18n = inject(LanguageService);

  readonly activeFarmService =
    inject(ActiveFarmService);

  readonly activeGameServerService =
    inject(ActiveGameServerService);

  readonly syncErrorOpen = signal(false);

  readonly hasSyncError = computed(
    () =>
      this.activeGameServerService.error() ||
      this.activeFarmService.error(),
  );

  /** HTTP status behind the current sync error, or null if unreachable. */
  readonly syncErrorStatus = computed(() => {
    if (!this.hasSyncError()) {
      return null;
    }

    return this.activeGameServerService.error()
      ? this.activeGameServerService.errorStatus()
      : this.activeFarmService.errorStatus();
  });

  /**
   * Translation key for a human explanation of the sync error, or null when
   * there is nothing meaningful to show (in which case the info button is
   * hidden).
   */
  readonly syncErrorMessageKey = computed<TranslationKey | null>(
    () => {
      if (!this.hasSyncError()) {
        return null;
      }

      const status = this.syncErrorStatus();

      if (status === null || status === 0) {
        return 'shell.syncError.unreachable';
      }

      if (status === 401 || status === 403) {
        return 'shell.syncError.auth';
      }

      if (status === 404) {
        return 'shell.syncError.notFound';
      }

      if (status >= 500) {
        return 'shell.syncError.server';
      }

      return 'shell.syncError.generic';
    },
  );

  constructor() {
    effect(() => {
      const gameServerId =
        this.activeGameServerService
          .activeGameServerId();

      if (gameServerId === null) {
        return;
      }

      this.activeFarmService.loadFarms();
    });

    this.activeGameServerService
      .loadGameServers();
  }

  openSyncError(): void {
    this.syncErrorOpen.set(true);
  }

  closeSyncError(): void {
    this.syncErrorOpen.set(false);
  }

  selectGameServer(
    gameServerId: string,
  ): void {
    const parsedGameServerId =
      Number(gameServerId);

    if (
      !Number.isInteger(
        parsedGameServerId,
      )
    ) {
      return;
    }

    this.activeGameServerService
      .selectGameServer(
        parsedGameServerId,
      );
  }

  selectFarm(farmId: string): void {
    const parsedFarmId = Number(farmId);

    if (!Number.isInteger(parsedFarmId)) {
      return;
    }

    this.activeFarmService.selectFarm(
      parsedFarmId,
    );
  }
}
